import React, { useState, useEffect } from "react";
import { Tabs, Tab, Box, Typography, Badge } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

// Tab Components
import DashboardTab from "./DashboardTab";
import FamilyTreeTab from "./FamilyTreeTab";
import OrdersTab from "./OrdersTab";
import AccountSettingsTab from "./AccountSettingsTab";
import HelpSupportTab from "./HelpSupportTab";
import GenerationalArtTab from "./GenerationalArtTab";
import MissionaryArtTab from "./Mission/MissionaryArtTab";

const AccountPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeOrderCount, setActiveOrderCount] = useState(0);

  // Map query parameter values to tab indices
  const tabMapping: Record<string, number> = {
    dashboard: 0,
    "family-tree": 1,
    "generational-form": 2,
    "missionary-form": 3,
    orders: 4,
    "account-settings": 5,
    help: 6,
  };

  // Map tab indices back to query parameter values
  const indexToTab: Record<number, string> = {
    0: "dashboard",
    1: "family-tree",
    2: "generational-form",
    3: "missionary-form",
    4: "orders",
    5: "account-settings",
    6: "help",
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoad(false);
    }, 500); // Adjust the duration to match your slide-down animation
    return () => clearTimeout(timer);
  }, []);

  // Handle URL query parameters on component mount and location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");

    if (tabParam && tabMapping[tabParam] !== undefined) {
      setSelectedTab(tabMapping[tabParam]);
    } else if (!tabParam) {
      // If no tab parameter, default to dashboard and update URL
      setSelectedTab(0);
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("tab", "dashboard");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, tabMapping, navigate, location.pathname]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);

    // Update URL with the corresponding query parameter
    const tabParam = indexToTab[newValue];
    if (tabParam) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("tab", tabParam);
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  };

  // Callback to update the badge count from OrdersTab
  const handleOrderCountChange = (count: number) => setActiveOrderCount(count);

  return (
    <Box sx={{ width: "100%", typography: "body1", padding: "36px" }}>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="account tabs"
        sx={{ marginBottom: "24px" }}
      >
        <Tab label="Dashboard" />
        <Tab label="Family Tree Info" />
        <Tab label="Generational Art" />
        <Tab label="Missionary Info" />
        <Tab
          label={
            <Badge color="primary" badgeContent={activeOrderCount}>
              Orders
            </Badge>
          }
        />
        <Tab label="Account Settings" />
        <Tab label="Help & Support" />
      </Tabs>

      {selectedTab === 0 && <DashboardTab />}
      {selectedTab === 1 && <FamilyTreeTab />}
      {selectedTab === 2 && <GenerationalArtTab />}
      {selectedTab === 3 && <MissionaryArtTab />}
      {selectedTab === 4 && (
        <OrdersTab onOrderCountChange={handleOrderCountChange} />
      )}
      {selectedTab === 5 && <AccountSettingsTab />}
      {selectedTab === 6 && <HelpSupportTab />}
    </Box>
  );
};

export default AccountPage;
